import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import i18next from 'i18next';
import '../../../i18n.js';
import { setStructureConfirmationProgressByExam } from '../../confirmation-progress.js';
import { Conversation } from '../../conversation.js';
import { Exam } from '../../exam.js';
import {
  confirmStructureMutation,
  getStructurePointInconsistencies,
  reopenStructureCanvas,
} from '../confirm-structure.js';
import { confirmSubmissionSplitMutation } from '../confirm-submission-split.js';
import { getExamStateByExamIdQuery } from '../get-exams.js';
import { resolveConversationLanguage } from '../utils/system-info.js';
type ConfirmationProgressState = { status?: string; confirmed?: boolean; messageId?: string | null } | undefined;

const isConfirmed = (state: ConfirmationProgressState): boolean =>
  Boolean(state?.confirmed || state?.status === 'DONE');

const getConfirmationState = async (examId: string, key: 'splitConfirmationState' | 'structureConfirmationState') => {
  const state = await getExamStateByExamIdQuery(examId)({
    [key]: { status: 1, confirmed: 1, messageId: 1 },
  });
  return (state as any)?.[key] as ConfirmationProgressState;
};

function splitConfirmationReply(language: string, confirmed: boolean): string {
  return i18next.t(confirmed ? 'ConfirmationReplies.split.confirmed' : 'ConfirmationReplies.split.pending', {
    lng: language,
  });
}

function structureConfirmationReply(
  language: string,
  masterSolutionUploaded: boolean,
  gradingSchemeUploaded: boolean,
): string {
  const upload =
    masterSolutionUploaded && gradingSchemeUploaded
      ? 'Sowohl die Musterlösung als auch das Bewertungsschema wurden hochgeladen. '
      : masterSolutionUploaded && !gradingSchemeUploaded
        ? 'Die Musterlösung wurde hochgeladen, das Bewertungsschema nicht. '
        : !masterSolutionUploaded && gradingSchemeUploaded
          ? 'Das Bewertungsschema wurde hochgeladen, die Musterlösung nicht. '
          : 'Weder die Musterlösung noch das Bewertungsschema wurden hochgeladen. ';

  const result = `Die Aufgabenstruktur wurde bestätigt. ${upload}Bedanke dich beim Nutzer, teile ihm mit, dass die Verarbeitung im Hintergrund fortgesetzt wird und führe ihn weiter durch den Prozess.`;

  return result;
}

export async function runSplitConfirmationAgent(
  confirmed: boolean,
  examId?: string,
  conversationId?: string,
): Promise<string> {
  if (!confirmed) {
    return i18next.t('ConfirmationReplies.errors.notConfirmed', { lng: 'de' });
  }
  if (!examId || !conversationId) {
    return i18next.t('ConfirmationReplies.errors.missingIdentifiers', { lng: 'de' });
  }

  const splitState = await getConfirmationState(examId, 'splitConfirmationState');
  const messageId = splitState?.messageId ?? undefined;
  if (!messageId) {
    const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });
    return i18next.t('ConfirmationReplies.errors.missingSplitMessage', { lng: conversationLanguage });
  }

  if (isConfirmed(splitState)) {
    logger.info('Split confirmation already confirmed. Returning success (idempotent).');
    const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });
    return splitConfirmationReply(conversationLanguage, true);
  }

  const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });

  try {
    await confirmSubmissionSplitMutation(examId, conversationId, messageId)();
  } catch (err) {
    logger.error({ err }, 'Failed to confirm exam split (agent)');
    const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });
    return i18next.t('ConfirmationReplies.errors.splitFailure', { lng: conversationLanguage });
  }

  logger.info('Split Confirmation Agent Finished');
  return splitConfirmationReply(conversationLanguage, true);
}

export async function runStructureConfirmationAgent(
  confirmed: boolean,
  examId?: string,
  conversationId?: string,
): Promise<string> {
  if (!confirmed) {
    return i18next.t('ConfirmationReplies.errors.notConfirmed', { lng: 'de' });
  }
  if (!examId || !conversationId) {
    return i18next.t('ConfirmationReplies.errors.missingIdentifiers', { lng: 'de' });
  }

  const structureState = await getConfirmationState(examId, 'structureConfirmationState');
  let structureMessageId = String(structureState?.messageId ?? '').trim();

  if (isConfirmed(structureState)) {
    logger.info('Structure confirmation already confirmed. Returning success (idempotent).');
  } else {
    // Block confirmation while points are assigned inconsistently: within a level, points must be
    // all-or-nothing, so a level where some tasks have points and others don't leaves those others
    // "missing". Re-open the canvas and tell the user which tasks need points (instead of confirming).
    const inconsistencies = await getStructurePointInconsistencies(examId);
    if (inconsistencies.length > 0) {
      const list = inconsistencies.map(i => `Aufgabe ${i.path.join('.')}`).join(', ');
      logger.info({ examId, list }, 'Structure confirmation via agent blocked: missing points');
      await reopenStructureCanvas(conversationId);
      return `Die Aufgabenstruktur konnte noch nicht bestätigt werden, weil bei einigen Aufgaben Punkte fehlen oder nicht übereinstimmen: ${list}. Der Struktur-Editor wurde wieder geöffnet.`;
    }

    try {
      const conversationCollection = await getCollection(Conversation);
      const conversation = await conversationCollection.findOne({ _id: conversationId });
      const messages = Array.isArray((conversation as any)?.messages) ? (conversation as any).messages : [];

      if (!structureMessageId) {
        const fallbackStructureMessage = [...messages]
          .reverse()
          .find(
            (message: any) =>
              message?._class === 'ConfirmationMessage' &&
              String(message?.confirmationType ?? 'structure').trim() === 'structure' &&
              !message?.confirmed,
          );

        structureMessageId = String(fallbackStructureMessage?._id ?? fallbackStructureMessage?.id ?? '').trim();

        if (structureMessageId) {
          await setStructureConfirmationProgressByExam(examId, {
            messageId: structureMessageId,
          });
        }
      }

      if (!structureMessageId) {
        const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });
        return i18next.t('ConfirmationReplies.errors.missingStructureMessage', { lng: conversationLanguage });
      }

      const structureMessage = Array.isArray((conversation as any)?.messages)
        ? (conversation as any).messages.find(
            (message: any) => String(message?._id ?? message?.id ?? '') === structureMessageId,
          )
        : null;
      const structureContent = typeof structureMessage?.content === 'string' ? structureMessage.content : '';

      if (!structureContent) {
        const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });
        return i18next.t('ConfirmationReplies.errors.structureContentMissing', { lng: conversationLanguage });
      }
      await confirmStructureMutation(examId, conversationId, structureMessageId, structureContent)();
    } catch (err) {
      logger.error({ err }, 'Failed to confirm structure via agent');
      const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });
      return i18next.t('ConfirmationReplies.errors.structureFailure', { lng: conversationLanguage });
    }
  }
  const conversationLanguage = await resolveConversationLanguage({ examId, conversationId });
  // The uploaded FILES, not a flag: the flag was only written when the extraction step started, i.e. by the
  // very pipeline this confirmation kicks off, so it could still read false for a file already uploaded.
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne(
    { _id: examId } as any,
    {
      projection: { masterSolutionIds: 1, gradingSchemeIds: 1 },
    } as any,
  );

  return structureConfirmationReply(
    conversationLanguage,
    ((exam as any)?.masterSolutionIds ?? []).length > 0,
    ((exam as any)?.gradingSchemeIds ?? []).length > 0,
  );
}
